import React, { MouseEvent, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import useOrderCostingVersion from "../../hooks/useOrderCostingVersion";
import { selectUser } from '../../store/selectors/UserSelectors';
import { approveOrder, cancelOrder, fetchOrderDetails, rejectOrder } from "../../store/action-creators/orders";
import { TRootState } from "../../store/reducers/RootReducer";
import { TOrdersByIdState } from "../../store/reducers/ordersById";
import { TApiSet } from "../../api/endpoints/Set";
import * as Api from "../../api";
import { find } from 'lodash';
import { TApiCountry } from "../../api/endpoints/Country";
import { TApiProvider } from "../../api/endpoints/Provider";
import RealtimeOrder from './RealtimeOrder';
import ArchiveOrder from './ArchiveOrder';
import WebOrder from './WebOrder';
import LoadingIndicator from "../../components/Feedback/LoadingIndicator";
import { OrderCategory, TApiOrderDetails } from "../../api/endpoints/Order";
import EuCutOutOrder from "./EuCutOutOrder";
import { TApiServicePackage } from "../../api/endpoints/ServicePackage";
import { TApiVolumeBand } from "../../api/endpoints/VolumeBand";
import { TApiServiceCharge } from "../../api/endpoints/ServiceCharge";

interface IRouteParams {
  id: string;
}

const Order = () => {
  const params = useParams<IRouteParams>();
  const dispatch = useDispatch();

  const user = useSelector(selectUser);

  const orderState = useSelector<TRootState, TOrdersByIdState[string] | undefined>(store => store.ordersById[params.id])
  const order = orderState?.data;

  const [submissionDialogOpen, setSubmissionDialogOpen] = useState<boolean>(false);
  const [sets, setSets] = useState<Array<TApiSet> | null>(null);
  const [countries, setCountries] = useState<Array<TApiCountry> | null>(null);
  const [providers, setProviders] = useState<Array<TApiProvider> | null>(null);
  const [servicePackages, setServicePackages] = useState<Array<TApiServicePackage> | null>(null);
  const [volumeBands, setVolumeBands] = useState<Array<TApiVolumeBand> | null>(null);
  const [serviceCharges, setServiceCharges] = useState<Array<TApiServiceCharge> | null>(null);
  const { orderCostingVersions, currentOrderCostingVersion } = useOrderCostingVersion(order);

  const onSubmitClick = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    setSubmissionDialogOpen(true);
  }, []);

  const onApprove = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    order && dispatch(approveOrder(order));
  }, [dispatch, order]);

  const onReject = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    order && dispatch(rejectOrder(order));
  }, [dispatch, order]);

  const onCancel = useCallback((event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    order && dispatch(cancelOrder(order));
  }, [dispatch, order]);

  const onSubmissionComplete = useCallback(() => {
    setSubmissionDialogOpen(false);
    window.scrollTo({ top: 0 });
  }, []);

  useEffect(() => {
    dispatch(fetchOrderDetails(params.id));
  }, [dispatch, params.id])

  useEffect(() => {
    Api.Set.list().then(setSets);
    Api.Provider.list().then(setProviders);
    Api.Country.list().then(setCountries);
  }, []);

  useEffect(() => {
    Api.VolumeBand.list().then(setVolumeBands);
    Api.ServiceCharge.list({ service_charge_model_id: currentOrderCostingVersion?.service_charge_model_id })
      .then(serviceCharges => {
        setServiceCharges(serviceCharges);

        Api.ServicePackage.list().then(servicePackages => {
          const supportedServicePackageIds = serviceCharges.reduce((acc, sc) => {
            acc.add(sc.service_package_id);

            return acc;
          }, new Set<number>());

          const supportedServicePackages = servicePackages.filter(sp => supportedServicePackageIds.has(sp.id));
          setServicePackages(supportedServicePackages);
        });
      });
  }, [currentOrderCostingVersion]);

  if (!order || orderState?.loading || !sets || !providers || !countries || !servicePackages || !volumeBands || !serviceCharges) {
    return <LoadingIndicator />;
  }

  const chosenProvider = find(providers, provider => provider.id === order.ccp_organisation);
  const chosenDataDeliverer = find(providers, provider => provider.id === order.data_deliverer_organisation_id);
  const chosenServicePackage = find(servicePackages, (sp) => sp.id === order.service_package_id);
  const chosenVolumeBand = find(volumeBands, vb => vb.id === order.volume_band_id);

  const commonProps = {
    user,
    sets,
    countries,
    providers,
    servicePackages,
    serviceCharges,
    chosenProvider,
    chosenDataDeliverer,
    chosenServicePackage,
    chosenVolumeBand,
    onSubmitClick,
    submissionDialogOpen,
    setSubmissionDialogOpen,
    onSubmissionComplete,
    onApprove,
    onReject,
    onCancel
  }

  if (order.category === OrderCategory.REALTIME) {
    return <RealtimeOrder key={order.id} order={order as TApiOrderDetails<OrderCategory.REALTIME>} {...commonProps} />;
  } else if (order.category === OrderCategory.ARCHIVE) {
    return <ArchiveOrder order={order as TApiOrderDetails<OrderCategory.ARCHIVE>} {...commonProps} />;
  } else if (order.category === OrderCategory.WEB) {
    return <WebOrder order={order as TApiOrderDetails<OrderCategory.WEB>} {...commonProps} />;
  } else if (order.category === OrderCategory.EU_CUTOUT) {
    return <EuCutOutOrder order={order as TApiOrderDetails<OrderCategory.EU_CUTOUT>} {...commonProps} />;
  } else {
    return null;
  }
}

export default Order;
